import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

// Claves de estado de UI que deben limpiarse cuando cambia el usuario logueado.
const TABS_KEY = "labiferia:openTabs:v1";
const LAST_USER_KEY = "labiferia:lastUser";

function clearUserScopedState() {
  try {
    localStorage.removeItem(TABS_KEY);
    window.dispatchEvent(new Event("tabschange"));
  } catch { /* noop */ }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        // Si el usuario recordado no coincide, limpiar UI heredada.
        const last = localStorage.getItem(LAST_USER_KEY);
        if (last && last !== String(data.id)) clearUserScopedState();
        localStorage.setItem(LAST_USER_KEY, String(data.id));
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    };
    check();
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    if (data.token) localStorage.setItem("erp_token", data.token);
    // Si el usuario que entra es distinto al anterior, borrar tabs abiertas.
    const last = localStorage.getItem(LAST_USER_KEY);
    if (last !== String(data.id)) clearUserScopedState();
    localStorage.setItem(LAST_USER_KEY, String(data.id));
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("erp_token");
    localStorage.removeItem(LAST_USER_KEY);
    clearUserScopedState();
    setUser(false);
  };

  const setFavoritos = async (favoritos) => {
    const { data } = await api.put("/auth/me/favoritos", { favoritos });
    setUser((u) => (u ? { ...u, favoritos: data.favoritos } : u));
    return data.favoritos;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setFavoritos }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
